import { requestRemoteState } from '../../components/Remote/deviceRemoteApi';
import { inputEvidenceFresh } from '../../utils/screenActivity';
import { resolveScreenshotTarget } from '../../components/AreaOverview/screenPreviewTarget';
import { evidenceFresh } from '../../utils/evidenceFreshness';
import { resolvePowerObservations } from '../../utils/powerObservations';

// A background summary can lag behind our last get_state reply. Preserve newer
// observations only within the same native registration/session and their real TTL.
export function retainVenueScreenReports(summary, previous) {
  const liveStates = summary?.playbackStates || {};
  const devices = new Map((summary?.playbackDevices || []).map((device) => [Number(device.id), device]));
  const previousDevices = new Map((previous?.playbackDevices || []).map((device) => [Number(device.id), device]));
  const nextStates = { ...liveStates };
  (summary?.screens || []).forEach((screen) => {
    const id = Number(screen.televisionDeviceId);
    const target = resolveScreenshotTarget({ activeDeviceId: id }, devices, liveStates, summary?.nativeBindings || {});
    const oldTarget = resolveScreenshotTarget({ activeDeviceId: id }, previousDevices, previous?.playbackStates || {}, previous?.nativeBindings || {});
    if (!target.deviceId || target.deviceId !== oldTarget.deviceId || target.capabilityKey !== oldTarget.capabilityKey) return;
    [id, target.deviceId].forEach((deviceId) => {
      const oldState = previous?.playbackStates?.[deviceId]?.state;
      const state = liveStates[deviceId]?.state;
      if (!state || !oldState || liveStates[deviceId].available === false || state.channels?.native?.connected !== true) return;
      const update = {};
      if (evidenceFresh(oldState.nativePowerObservation)
        && Number(oldState.nativePowerObservation.collectedAt) > Number(state.nativePowerObservation?.collectedAt || 0)) {
        update.nativePowerObservation = oldState.nativePowerObservation;
      }
      const inputTime = (report) => Number(report?.activeSource?.collectedAt ?? report?.collectedAt ?? 0);
      if (inputEvidenceFresh(oldState.tvInputManager) && inputTime(oldState.tvInputManager) > inputTime(state.tvInputManager)) {
        update.tvInputManager = oldState.tvInputManager;
      }
      nextStates[deviceId] = { ...liveStates[deviceId], state: { ...state, ...update } };
    });
  });
  return { ...summary, playbackStates: nextStates };
}

// Refresh aging input AND power observations. A connected TV with an expired
// display sample otherwise becomes "Screen status unknown" between summary polls.
export async function refreshVenueInputReports(summary) {
  const liveStates = summary?.playbackStates || {};
  const devices = new Map((summary?.playbackDevices || []).map((device) => [Number(device.id), device]));
  const requests = new Map();
  const updates = await Promise.all((summary?.screens || []).map(async (screen) => {
    const id = Number(screen.televisionDeviceId);
    // Reuse validated native identity; screenshot support does not gate an input-only read.
    const target = resolveScreenshotTarget({ activeDeviceId: id }, devices, liveStates, summary?.nativeBindings || {});
    const nativeId = target.deviceId;
    const live = liveStates[nativeId];
    if (!id || !nativeId || live?.state?.channels?.native?.connected !== true) return null;
    const previous = liveStates[id]?.state?.tvInputManager;
    const refreshAt = Date.now() + 10000;
    const power = resolvePowerObservations(liveStates[id], null, refreshAt);
    const powerReady = screen.displayKind === 'unmanaged' || power.fields.displayState || power.fields.interactive;
    if (inputEvidenceFresh(previous, refreshAt) && powerReady) return null;
    if (!requests.has(nativeId)) {
      requests.set(nativeId, requestRemoteState({ targetDeviceId: nativeId, includeScreenshot: false }, { timeout: 8000 })
        .then((response) => response?.state).catch(() => null));
    }
    const state = await requests.get(nativeId);
    const update = {};
    if (inputEvidenceFresh(state?.tvInputManager)) update.tvInputManager = state.tvInputManager;
    const powerObservation = state?.nativePowerObservation || state?.externalApp?.powerObservation;
    if (evidenceFresh(powerObservation)) update.nativePowerObservation = powerObservation;
    else if (evidenceFresh(state?.externalApp) && typeof state.externalApp.displayInteractive === 'boolean') {
      update.nativePowerObservation = { collectedAt: state.externalApp.collectedAt, provider: 'native',
        interactive: { status: 'observed', value: state.externalApp.displayInteractive } };
    }
    // get_state.externalApp contains power only. Never renew cached foreground
    // telemetry by merging that object's collectedAt into the foreground report.
    return { id, nativeId, update };
  }));
  const nextStates = { ...liveStates };
  updates.filter(Boolean).forEach(({ id, nativeId, update }) => {
    [id, nativeId].forEach((targetId) => {
      nextStates[targetId] = { ...liveStates[targetId], state: { ...nextStates[targetId]?.state, ...update } };
    });
  });
  return { ...summary, playbackStates: nextStates };
}
